import axiosInstance from "@app/axiosInstance";
import { T_Company } from "@app/entities/company/types";
import { useCompany } from "@app/providers/CompanyContextProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import notification from "antd/lib/notification";
import axios from "axios";

export const useCompanyMutations = () => {
  const queryClient = useQueryClient();
  const { selectedCompany, setSelectedCompanyId } = useCompany();
  const companyId = selectedCompany?.companyId;

  const { mutate: joinCompany, isPending: isJoinCompanyPending } = useMutation({
    mutationFn: (payload: { adminActivationCode: string }) =>
      axiosInstance
        .post("/admin/company/join", payload)
        .then((response) => response.data),
    onSuccess: ({ companyId }) => {
      notification.success({
        description: "Successfully joined association",
        title: "Success",
      });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("companies"),
      });
      setSelectedCompanyId(companyId);
    },
    onError: (error) => {
      if (error && axios.isAxiosError(error)) {
        switch (error.response?.status) {
          case 404:
            notification.error({
              description: "No association was foud with this activation code",
              title: "Error",
            });
            break;
          case 409:
            notification.error({
              description: "You are already a member of this association",
              title: "Error",
            });
            break;
          default:
            notification.error({
              description: error.message ?? "Failed to join association",
              title: "Error",
            });
        }
        return;
      }
      notification.error({
        description: error.message ?? "Failed to join association",
        title: "Error",
      });
    },
  });

  const {
    mutate: refreshAdminActivationCode,
    isPending: isRefreshAdminActivationCodePending,
  } = useMutation({
    mutationFn: () =>
      axiosInstance.get(`/admin/company/${companyId}/refresh-admin`),
    onError: (_error) => {
      notification.error({ title:"Failed to refresh activation code" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("companies"),
      });
      notification.success({
        description: "Activation code has been refreshed",
        title: "Success",
      });
    },
  });

  const {
    mutate: refreshMemberActivationCode,
    isPending: isRefreshMemberActivationCodePending,
  } = useMutation({
    mutationFn: () =>
      axiosInstance.get(`/admin/company/${companyId}/refresh-member`),
    onError: (_error) => {
      notification.error({ title:"Failed to refresh activation code" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("companies"),
      });
      notification.success({
        description: "Activation code has been refreshed",
        title: "Success",
      });
    },
  });

  const {
    mutate: deactivateAllMembers,
    isPending: isDeactivateAllMembersPending,
  } = useMutation({
    mutationFn: () =>
      axiosInstance.delete(`/admin/${companyId}/member/deactivate`),
    onError: (_error) => {
      notification.error({ title:"Failed to deactivate all members" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company", companyId] });
      notification.success({
        description: "All members have been deactivated",
        title: "Success",
      });
    },
  });

  const {
    mutate: emailDeactivatedMembers,
    isPending: isEmailDeactivatedMembersPending,
  } = useMutation({
    mutationFn: ({ subject, message }: { subject: string; message: string }) =>
      axiosInstance.post(`/admin/${companyId}/member/email-deactivated`, {
        subject,
        message,
      }),
    onError: (_error) => {
      notification.error({ title:"Failed to send emails to deactivated members" });
    },
    onSuccess: () => {
      notification.success({
        description: "Emails sent to all deactivated members",
        title: "Success",
      });
    },
  });

  const {
    mutate: updateNotifications,
    isPending: isUpdateNotificationsPending,
  } = useMutation({
    mutationFn: ({
      lowBalanceEmail,
      staleReportsEmail,
      lowBalanceThreshold,
    }: {
      lowBalanceEmail: string;
      staleReportsEmail: string;
      lowBalanceThreshold: number;
    }) =>
      axiosInstance.put(`/admin/company/${companyId}/notifications`, {
        lowBalanceEmail,
        staleReportsEmail,
        lowBalanceThreshold,
      }),
    onError: (_error) => {
      notification.error({
        title: "Failed to update notification preferences",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("companies"),
      });
      notification.success({
        description: "Notification preferences have been updated",
        title: "Success",
      });
    },
  });

  const { mutate: checkout, isPending: isCheckoutPending } = useMutation({
    mutationFn: (checkoutData: {
      cancelUrl: string;
      companyId: number;
      successUrl: string;
    }) =>
      axiosInstance
        .post("/stripe/checkout", checkoutData)
        .then((response) => response.data),
    onSuccess: async ({ url }) => {
      if (!url) {
        throw new Error("Failed to create session");
      }
      window.location.href = url;
    },
    onError: (_error) => {
      notification.error({ title:"Failed to create checkout session" });
    },
  });

  const { mutate: registerCompany, isPending: isRegisterCompanyPending } =
    useMutation({
      mutationFn: (companyData: T_Company) =>
        axiosInstance
          .post("/admin/company", companyData)
          .then((response) => response.data),
      onError: (_error) => {
        notification.error({ title:"Failed to register company" });
      },
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
        notification.success({
          description: "Company has been registered",
          title: "Success",
        });
      },
    });

  const { mutate: updateCompany, isPending: isUpdateCompanyPending } =
    useMutation({
      mutationFn: (companyData: T_Company) =>
        axiosInstance.put(`/admin/company/${companyId}`, companyData),
      onError: (_error) => {
        notification.error({ title:"Failed to update company" });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes("companies"),
        });
        notification.success({
          description: "Company has been updated",
          title: "Success",
        });
      },
    });

  const isMutationPending =
    isRefreshMemberActivationCodePending ||
    isDeactivateAllMembersPending ||
    isEmailDeactivatedMembersPending ||
    isUpdateNotificationsPending ||
    isRefreshAdminActivationCodePending ||
    isCheckoutPending ||
    isRegisterCompanyPending ||
    isJoinCompanyPending ||
    isUpdateCompanyPending;
  return {
    isMutationPending,
    checkout,
    deactivateAllMembers,
    emailDeactivatedMembers,
    joinCompany,
    refreshAdminActivationCode,
    refreshMemberActivationCode,
    registerCompany,
    updateNotifications,
    updateCompany,
  };
};
